import { useState } from 'react';
import Button from '@mui/material/Button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EmailResendResultSchema, type NotificationDeliveryDto } from '@taqon/contracts';
import { errorMessage, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';

/** The delivery log's query key prefix, so a resend refreshes every page of it. */
const DELIVERY_LOG_KEY = ['platform', 'notification-deliveries'] as const;

/**
 * Resend a failed e-mail from the delivery log (FRD-INT-014). Shown only on a failed e-mail and
 * only to a session that may change notification delivery (`platform.notification_rule:change`);
 * the API decides. The same notification is queued again — nothing new is created — and the
 * outcome appears in the log once the worker has tried it (FRD-INT-011).
 */
export function ResendEmailButton({ delivery }: { delivery: NotificationDeliveryDto }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const resend = useMutation({
    mutationFn: () =>
      postJson(
        `/api/v1/platform/notifications/${delivery.id}/resend`,
        {},
        EmailResendResultSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: DELIVERY_LOG_KEY });
    },
  });

  if (
    delivery.channel !== 'email' ||
    !hasCapability(session.data, 'platform.notification_rule:change')
  ) {
    return null;
  }
  // Once resent the row is no longer failed and the button goes; the dialog stays mounted so its
  // closing transition can finish and return focus.
  return (
    <>
      {delivery.deliveryStatus === 'failed' && (
        <Button
          size="small"
          onClick={() => {
            resend.reset();
            setOpen(true);
          }}
          aria-label={`Resend e-mail “${delivery.title}” to ${delivery.recipientName}`}
        >
          Resend
        </Button>
      )}
      <ConfirmDialog
        open={open}
        title="Resend this e-mail?"
        message={
          <>
            “{delivery.title}” to {delivery.recipientName} failed after {delivery.attempts}{' '}
            {delivery.attempts === 1 ? 'attempt' : 'attempts'}
            {delivery.failureReason === null ? '' : ` (${delivery.failureReason})`}. It will be
            queued again as the same notification and its outcome shown here. The resend is recorded
            in the audit trail.
          </>
        }
        confirmLabel="Resend"
        busy={resend.isPending}
        error={resend.isError ? errorMessage(resend.error) : null}
        onCancel={() => {
          setOpen(false);
        }}
        onConfirm={() => {
          resend.mutate();
        }}
      />
    </>
  );
}
